import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC, ReactNode } from 'react';
import { ModalBodyWrapper } from 'src/components/form/form/ModalBodyWrapper';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import {
  useInsertDocumentAssessmentResult,
  useUpdateDocumentAssessmentResult,
} from '@/hooks/mutations/assessment-result';
import { useMultiParentFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useGetDocumentAssessmentResultById } from '@/hooks/queries';
import { saveFilesForOptionalCreatedEntity } from '@/utils/createWithFiles';
import {
  assessmentResultsUrl,
  policyDetailsUrl,
  policyRegisterUrl,
} from '@/utils/urls';

import DocumentAssessmentResultForm from './DocumentAssessmentResultForm';
import type { DocumentAssessmentResultFormDataFields } from './documentAssessmentResultSchema';
import { defaultValues } from './documentAssessmentResultSchema';
import { isResultFormReadOnly } from './resultFormReadOnly';

type Props = {
  readonly: boolean;
  navigateToResults: boolean;
  isModalForm: boolean;
  parentId?: string;
  assessedItem?: ObjectWithContributors;
  id?: string;
  onDismiss?: (saved: boolean) => void;
  beforeFieldsSlot?: ReactNode;
  showAssessmentSelector?: boolean;
  documentIds?: string[];
  header?: string;
};

const ConnectedDocumentAssessmentResultForm: FC<Props> = ({
  readonly,
  parentId,
  assessedItem,
  id,
  onDismiss,
  beforeFieldsSlot,
  showAssessmentSelector,
  navigateToResults,
  isModalForm,
  documentIds,
  header,
}) => {
  const { data } = useGetDocumentAssessmentResultById({
    queryArgs: { id: id ?? '' },
    shouldSkip: !id,
  });
  const documentAssessmentResult = data?.document_assessment_result?.[0];
  // The update endpoint accepts either a type-level grant or permission on the
  // result node itself. Supplying the opened result makes the V3 permission
  // hook exercise that same instance arm instead of checking only the type
  // grant. The by-id query does not load legacy ancestry, so an empty list
  // preserves the previous V2 type-level behaviour while still giving V3 the
  // result Id it needs.
  const permissionTarget = documentAssessmentResult
    ? { Id: documentAssessmentResult.Id, ancestorContributors: [] }
    : undefined;
  const {
    hasPermission: canUpdateDocumentAssessmentResult,
    loading: isLoadingUpdateDocumentAssessmentResult,
  } = useHasPermissionQuery(
    'update:document_assessment_result',
    permissionTarget
  );

  const { updateFiles } = useMultiParentFileUpdate();
  const { insertDocumentAssessmentResult } =
    useInsertDocumentAssessmentResult();
  const { updateDocumentAssessmentResult } =
    useUpdateDocumentAssessmentResult();

  documentIds = documentIds ?? [];
  const parentDocument = documentAssessmentResult?.parents.find(
    (p) => p.document
  );
  if (parentDocument?.document?.Id) {
    documentIds.push(parentDocument.document.Id);
  } else if (assessedItem?.Id) {
    documentIds.push(assessedItem.Id);
  }

  const onSave = async (values: DocumentAssessmentResultFormDataFields) => {
    const { files } = values;
    const assessmentId = parentId || values.AssessmentId || undefined;
    const documentAssessmentResultIds: string[] = [];
    if (documentAssessmentResult) {
      const result = await updateDocumentAssessmentResult({
        Id: documentAssessmentResult.Id,
        // Optimistic-concurrency token: the snapshot this form was loaded from.
        // A second editor saving over a newer rating is rejected with a 409
        // rather than silently winning (COI-622).
        OriginalTimestamp: documentAssessmentResult.ModifiedAtTimestamp,
        Rating: values.Rating,
        Rationale: values.Rationale ?? null,
        TestDate: values.TestDate ?? null,
        CustomAttributeData: values.CustomAttributeData ?? null,
      });

      if (result.update_document_assessment_result?.affected_rows === 0) {
        throw new Error('Document assessment result update failed');
      }
      documentAssessmentResultIds.push(id!);
    } else {
      const result = await insertDocumentAssessmentResult({
        Rating: values.Rating,
        Rationale: values.Rationale ?? null,
        TestDate: values.TestDate ?? null,
        CustomAttributeData: values.CustomAttributeData ?? null,
        AssessmentId: assessmentId,
        DocumentIds: (values.DocumentIds ?? []).map((r) => r.value),
      });

      if (!result.insertChildDocumentAssessmentResult?.Ids?.length) {
        throw new Error('Document assessment result id is missing');
      }
      documentAssessmentResultIds.push(
        ...result.insertChildDocumentAssessmentResult.Ids
      );
    }

    const createdEntity = !documentAssessmentResult
      ? {
          id: documentAssessmentResultIds[0],
          destination: assessmentId
            ? assessmentResultsUrl(assessmentId)
            : assessedItem
              ? policyDetailsUrl(assessedItem.Id)
              : policyRegisterUrl(),
        }
      : undefined;
    const saveFiles = () =>
      updateFiles({
        parentIds: documentAssessmentResultIds,
        parentType: Parent_Type_Enum.DocumentAssessmentResult,
        selectedFiles: files,
        originalFiles: documentAssessmentResult?.files.map((f) => f.file) ?? [],
      });
    await saveFilesForOptionalCreatedEntity({ createdEntity, saveFiles });

    return {
      savedIds: documentAssessmentResultIds,
      createdEntity,
      successNavigation:
        navigateToResults && assessmentId
          ? { destination: assessmentResultsUrl(assessmentId) }
          : undefined,
    };
  };

  return (
    <DocumentAssessmentResultForm
      header={header}
      defaultValues={{
        ...defaultValues,
        DocumentIds: documentIds.map((c) => ({ value: c })),
        AssessmentId: parentId ?? null,
      }}
      values={
        documentAssessmentResult
          ? {
              Rating: documentAssessmentResult?.Rating ?? 1,
              Rationale: documentAssessmentResult?.Rationale ?? '',
              AssessmentId:
                documentAssessmentResult?.parents.find((p) => p.assessment)
                  ?.assessment?.Id ?? null,
              DocumentIds: documentIds.map((c) => ({ value: c })),
              TestDate: documentAssessmentResult?.TestDate,
              CustomAttributeData:
                documentAssessmentResult?.CustomAttributeData,
              files: documentAssessmentResult?.files.map((f) => f.file) ?? [],
            }
          : undefined
      }
      onDismiss={onDismiss}
      onSave={onSave}
      readOnly={isResultFormReadOnly({
        readonly,
        permissionLoading: isLoadingUpdateDocumentAssessmentResult,
        canUpdate: canUpdateDocumentAssessmentResult,
      })}
      renderTemplate={(renderProps) =>
        isModalForm ? (
          <ModalBodyWrapper {...renderProps} />
        ) : (
          <PageWrapper {...renderProps} />
        )
      }
      disableDocumentSelector={id != undefined}
      beforeFieldsSlot={beforeFieldsSlot}
      showSelector={showAssessmentSelector ? 'rating' : undefined}
      assessmentMode={'rating'}
    />
  );
};

export default ConnectedDocumentAssessmentResultForm;
